import axios from "axios";
import { useCallback, useState } from "react";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { DCDesignWallTile } from "./types";

const gqlEndpoint = `${environment.baseUrl}/graphql`;

const WALL_TILE_FIELDS = `
  id design_id design_version_id scale_option_id scale_value
  scale_width_inches scale_height_inches wall_width_ft wall_height_ft
  is_custom_wall repeats_x repeats_y output_width_px output_height_px
  image_path image_url file_name created_at updated_at
`;

const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
  const { data } = await axios.post(gqlEndpoint, { query, variables }, {
    headers: { "X-Wallmates-Auth": authToken }
  });
  if (data.errors) throw new Error(data.errors[0]?.message || "GraphQL error");
  return data.data;
};

export const useDesignWallTiles = () => {
  const [, setSnackbar] = useSnackbar();
  const [, authToken, loginFunctions] = useLogin();
  const [wallTiles, setWallTiles] = useState<DCDesignWallTile[]>([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listWallTiles = useCallback(async (designId: number) => {
    if (!designId) return null;
    try {
      setLoading(true);
      setError(null);
      const q = `query($design_id:ID!){ dcDesignWallTiles(design_id:$design_id){ ${WALL_TILE_FIELDS} } }`;
      const data = await graphqlRequest(authToken, q, { design_id: designId });
      const next = (data?.dcDesignWallTiles || []) as DCDesignWallTile[];
      setWallTiles(next);
      return next;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setError(err?.message || "Failed to load wall tiles.");
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to load wall tiles." });
      return null;
    } finally {
      setLoading(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  // Generation is synchronous server-side — no polling needed; the saved row comes
  // straight back and is merged into state (replacing any row for the same combo).
  const generateWallTile = useCallback(async (args: {
    designId: number;
    scaleOptionId: number;
    wallWidthFt: number;
    wallHeightFt: number;
  }) => {
    const { designId, scaleOptionId, wallWidthFt, wallHeightFt } = args;
    if (!designId || !scaleOptionId) return null;
    try {
      setGenerating(true);
      setError(null);
      const mutation = `mutation($input:DCDesignWallTileGenerateInput!){ generateDCDesignWallTile(input:$input){ ${WALL_TILE_FIELDS} } }`;
      const data = await graphqlRequest(authToken, mutation, {
        input: {
          design_id: designId,
          scale_option_id: scaleOptionId,
          wall_width_ft: wallWidthFt,
          wall_height_ft: wallHeightFt
        }
      });
      const created = data?.generateDCDesignWallTile as DCDesignWallTile | undefined;
      if (created) {
        setWallTiles((prev) => [created, ...prev.filter((tile) => Number(tile.id) !== Number(created.id))]);
        setSnackbar({ show: true, snackbarLevel: "success", text: "Wall tile generated." });
      }
      return created ?? null;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setError(err?.message || "Failed to generate wall tile.");
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to generate wall tile." });
      return null;
    } finally {
      setGenerating(false);
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  const deleteWallTile = useCallback(async (id: number, designId: number) => {
    if (!id || !designId) return false;
    try {
      setError(null);
      const mutation = `mutation($id:ID!,$design_id:ID!){ deleteDCDesignWallTile(id:$id, design_id:$design_id) }`;
      await graphqlRequest(authToken, mutation, { id, design_id: designId });
      setWallTiles((prev) => prev.filter((tile) => Number(tile.id) !== Number(id)));
      setSnackbar({ show: true, snackbarLevel: "success", text: "Wall tile deleted." });
      return true;
    } catch (err: any) {
      errorCatcher(err, loginFunctions.logout);
      setError(err?.message || "Failed to delete wall tile.");
      setSnackbar({ show: true, snackbarLevel: "error", text: err?.message || "Failed to delete wall tile." });
      return false;
    }
  }, [authToken, loginFunctions.logout, setSnackbar]);

  return {
    wallTiles,
    loading,
    generating,
    error,
    listWallTiles,
    generateWallTile,
    deleteWallTile
  };
};
